// client/src/components/common/LanguageSelector.js

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const LanguageSelector = ({ userType, currentLanguage, onLanguageChange }) => {
  const { t, i18n } = useTranslation('common');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLanguageChange = async (newLanguage) => {
    if (newLanguage === currentLanguage) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const endpoint = userType === 'wholesaler' 
        ? 'wholesaler/language' 
        : 'business/language';
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const fullUrl = `${API_URL}/${endpoint}`;
      
      await axios.put(fullUrl, 
        { language: newLanguage },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update i18n language
      await i18n.changeLanguage(newLanguage);

      // Call parent callback
      if (onLanguageChange) {
        onLanguageChange(newLanguage);
      }

      setSuccess(t('languageSelector.languageUpdated'));
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Language update error:', err);
      setError(t('languageSelector.languageError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <div className="flex flex-col space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            🌍 {t('languageSelector.title')}
          </h2>
          <p className="text-sm text-gray-600">
            {t('languageSelector.chooseLanguage')}
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
            ✓ {success}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            ✗ {error}
          </div>
        )}

        {/* Language Options */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Greek Option */}
          <button
            onClick={() => handleLanguageChange('el')}
            disabled={loading}
            className={`
              relative flex items-center justify-between p-4 rounded-lg border-2 transition-all
              ${currentLanguage === 'el'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 bg-white'
              }
              ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <div className="flex items-center space-x-3">
              <span className="text-3xl">🇬🇷</span>
              <div className="text-left">
                <div className="font-semibold text-gray-900">
                  {t('languageSelector.greekLabel')}
                </div>
                <div className="text-sm text-gray-600">
                  {t('languageSelector.greekSubLabel')}
                </div>
              </div>
            </div>
            {currentLanguage === 'el' && (
              <div className="text-blue-600">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </button>

          {/* English Option */}
          <button
            onClick={() => handleLanguageChange('en')}
            disabled={loading}
            className={`
              relative flex items-center justify-between p-4 rounded-lg border-2 transition-all
              ${currentLanguage === 'en'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 bg-white'
              }
              ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <div className="flex items-center space-x-3">
              <span className="text-3xl">🇬🇧</span>
              <div className="text-left">
                <div className="font-semibold text-gray-900">
                  {t('languageSelector.englishLabel')}
                </div>
                <div className="text-sm text-gray-600">
                  {t('languageSelector.englishSubLabel')}
                </div>
              </div>
            </div>
            {currentLanguage === 'en' && (
              <div className="text-blue-600">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </button>
        </div>

        {/* Info Note */}
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
          ℹ️ {t('languageSelector.note')}
        </div>
      </div>
    </div>
  );
};

export default LanguageSelector;